import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Skeleton,
  TableBody as MuiTableBody,
  TableCell,
  TableRow as MuiTableRow,
  Typography,
} from '@mui/material'

import { AutocompleteOption } from '@types'

import { CommandConfigRowData } from './CommandConfigTable'
import { BODY_VIEW, SKELETON_ROWS } from './consts'
import { TableRow } from './TableRow'

type TableBodyProps = {
  isLoading?: boolean
  tableRows: Array<{ row: CommandConfigRowData; index: number; fieldId: string }>
  bodyView?: BODY_VIEW
  isDisabled?: boolean
  namePrefix: string
  defaultRefFieldsObjectOption: AutocompleteOption<string> | null
  refObjectOptions?: AutocompleteOption[]
  isChangeRefObjectAllowed?: boolean
  onEditJs?: (index: number) => void
  typeOptions: AutocompleteOption[]
  hiddenRowIndexSet?: Set<number> | null
}

export const TableBody = ({
  isLoading,
  bodyView,
  tableRows,
  isDisabled,
  namePrefix,
  defaultRefFieldsObjectOption,
  refObjectOptions,
  isChangeRefObjectAllowed,
  onEditJs,
  typeOptions,
  hiddenRowIndexSet,
}: TableBodyProps) => {
  const { t } = useTranslation()

  const colSpan = 6

  const hasAnyRows = tableRows.length > 0

  const hasVisibleRows =
    hiddenRowIndexSet == null
      ? hasAnyRows
      : tableRows.some(({ index }) => !hiddenRowIndexSet.has(index))

  const shouldShowEmpty =
    !hasAnyRows || (bodyView === 'configured' && hiddenRowIndexSet != null && !hasVisibleRows)

  const skeletonRowIndexes = useMemo(
    () => Array.from({ length: SKELETON_ROWS }, (_, index) => index),
    []
  )
  const skeletonCellIndexes = useMemo(
    () => Array.from({ length: colSpan }, (_, index) => index),
    [colSpan]
  )

  return (
    <MuiTableBody>
      {isLoading ? (
        skeletonRowIndexes.map(rowIndex => (
          <MuiTableRow key={`sk-row-${rowIndex}`} hover={false}>
            {skeletonCellIndexes.map(cellIndex => (
              <TableCell key={`sk-cell-${rowIndex}-${cellIndex}`}>
                <Skeleton variant='text' />
              </TableCell>
            ))}
          </MuiTableRow>
        ))
      ) : shouldShowEmpty ? (
        <MuiTableRow hover={false}>
          <TableCell colSpan={colSpan} sx={{ py: 2, px: 2, textAlign: 'center' }}>
            <Typography color='text.secondary' variant='body2'>
              {t('commandParameters.table.empty')}
            </Typography>
          </TableCell>
        </MuiTableRow>
      ) : (
        tableRows.map(({ row, index, fieldId }) => (
          <TableRow
            key={fieldId}
            defaultRefFieldsObjectOption={defaultRefFieldsObjectOption}
            index={index}
            isChangeRefObjectAllowed={isChangeRefObjectAllowed}
            isDisabled={isDisabled}
            isHidden={hiddenRowIndexSet?.has(index) ?? false}
            namePrefix={namePrefix}
            refObjectOptions={refObjectOptions}
            row={row}
            typeOptions={typeOptions}
            onEditJs={onEditJs}
          />
        ))
      )}
    </MuiTableBody>
  )
}
